(() => {
  const elementName = 'gh-testimonial-slider';

  if (customElements.get(elementName)) return;

  class GhTestimonialSlider extends HTMLElement {
    constructor() {
      super();

      this.currentPage = 0;
      this.pages = [];
      this.scrollFrame = null;
      this.resizeObserver = null;

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.refresh = this.refresh.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-gh-category-viewport]');
      this.track = this.querySelector('[data-gh-category-track]');
      this.slides = [...this.querySelectorAll('[data-gh-category-slide]')];
      this.previousButton = this.querySelector('[data-gh-category-previous]');
      this.nextButton = this.querySelector('[data-gh-category-next]');
      this.controls = this.querySelector('[data-gh-category-controls]');
      this.progress = this.querySelector('[data-gh-category-progress]');
      this.currentOutput = this.querySelector('[data-gh-category-current]');
      this.totalOutput = this.querySelector('[data-gh-category-total]');
      this.status = this.querySelector('[data-gh-category-status]');
      this.progressEnabled = this.dataset.progressEnabled === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.viewport || !this.track) return;

      this.previousButton?.addEventListener('click', this.handlePreviousClick);
      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.viewport.addEventListener('keydown', this.handleKeydown);
      this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
      this.addEventListener('shopify:block:select', this.handleBlockSelect);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.refresh);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.refresh);
      }

      requestAnimationFrame(this.refresh);
    }

    disconnectedCallback() {
      if (!this.viewport) return;

      this.viewport.removeEventListener('keydown', this.handleKeydown);
      this.viewport.removeEventListener('scroll', this.handleScroll);
      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.resizeObserver?.disconnect();
      window.removeEventListener('resize', this.refresh);

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    refresh() {
      this.slides = [...this.querySelectorAll('[data-gh-category-slide]')];

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const hasOverflow = maxScroll > 1 && this.slides.length > 1;
      const pages = [];

      if (this.slides.length) {
        pages.push({ slide: this.slides[0], slideIndex: 0, align: 'start' });

        if (hasOverflow) {
          this.slides.slice(1).forEach((slide, index) => {
            const slideIndex = index + 1;
            const offset = this.getSlideOffset(slide);

            if (offset < maxScroll - 1) {
              pages.push({ slide, slideIndex, align: 'start' });
            }
          });

          const finalSlide = this.slides[this.slides.length - 1];
          const finalPage = pages[pages.length - 1];

          if (finalPage.slide === finalSlide && Math.abs(this.getSlideOffset(finalSlide) - maxScroll) <= 1) {
            finalPage.align = 'end';
          } else {
            pages.push({ slide: finalSlide, slideIndex: this.slides.length - 1, align: 'end' });
          }
        }
      }

      this.pages = pages.length ? pages : [{ slide: null, slideIndex: 0, align: 'start' }];
      this.toggleAttribute('data-has-overflow', hasOverflow);

      if (this.previousButton) this.previousButton.hidden = !hasOverflow;
      if (this.nextButton) this.nextButton.hidden = !hasOverflow;
      if (this.controls) this.controls.hidden = !hasOverflow && !this.progressEnabled;

      this.updateCurrentPage();
    }

    getSlideOffset(slide) {
      if (!this.slides.length || !slide) return 0;

      const firstRect = this.slides[0].getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const isRtl = getComputedStyle(this.viewport).direction === 'rtl';

      return isRtl
        ? Math.abs(firstRect.right - slideRect.right)
        : Math.abs(slideRect.left - firstRect.left);
    }

    handleScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateCurrentPage();
      });
    }

    handlePreviousClick() {
      this.goToPage(this.currentPage - 1);
    }

    handleNextClick() {
      this.goToPage(this.currentPage + 1);
    }

    handleKeydown(event) {
      const isRtl = getComputedStyle(this.viewport).direction === 'rtl';
      let nextPage = null;

      switch (event.key) {
        case 'ArrowLeft':
          nextPage = this.currentPage + (isRtl ? 1 : -1);
          break;
        case 'ArrowRight':
          nextPage = this.currentPage + (isRtl ? -1 : 1);
          break;
        case 'Home':
          nextPage = 0;
          break;
        case 'End':
          nextPage = this.pages.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.goToPage(nextPage);
    }

    handleBlockSelect(event) {
      const slide = event.target.closest?.('[data-gh-category-slide]');
      if (!slide || !this.contains(slide)) return;

      const slideIndex = this.slides.indexOf(slide);
      if (slideIndex < 0) return;

      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const isFullyVisible = slideRect.left >= viewportRect.left && slideRect.right <= viewportRect.right;

      if (isFullyVisible) return;

      const pageIndex = this.pages.findIndex((page) => page.slideIndex >= slideIndex);
      this.goToPage(pageIndex < 0 ? this.pages.length - 1 : pageIndex);
    }

    goToPage(pageIndex) {
      const boundedPage = Math.max(0, Math.min(pageIndex, this.pages.length - 1));
      const page = this.pages[boundedPage];
      if (!page?.slide) return;

      page.slide.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: page.align
      });

      this.currentPage = boundedPage;
      this.updateControls();
    }

    updateCurrentPage() {
      if (!this.slides.length || !this.pages.length) {
        this.currentPage = 0;
        this.updateControls();
        return;
      }

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const logicalScrollPosition = this.getLogicalScrollPosition(maxScroll);
      let nearestPage = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.pages.forEach((page, index) => {
        if (!page.slide) return;

        const targetOffset = page.align === 'end'
          ? maxScroll
          : Math.min(this.getSlideOffset(page.slide), maxScroll);
        const distance = Math.abs(logicalScrollPosition - targetOffset);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPage = index;
        }
      });

      this.currentPage = nearestPage;
      this.updateControls();
    }

    getLogicalScrollPosition(maxScroll) {
      const firstSlide = this.slides[0];
      if (!firstSlide) return 0;

      const viewportRect = this.viewport.getBoundingClientRect();
      const firstRect = firstSlide.getBoundingClientRect();
      const trackStyles = getComputedStyle(this.track);
      const startPadding = Number.parseFloat(trackStyles.paddingInlineStart) || 0;
      const isRtl = getComputedStyle(this.viewport).direction === 'rtl';
      const position = isRtl
        ? startPadding - (viewportRect.right - firstRect.right)
        : startPadding - (firstRect.left - viewportRect.left);

      return Math.max(0, Math.min(position, maxScroll));
    }

    updateControls() {
      const totalPages = Math.max(1, this.pages.length);
      const currentPageNumber = Math.min(this.currentPage + 1, totalPages);

      this.setButtonState(this.previousButton, currentPageNumber === 1);
      this.setButtonState(this.nextButton, currentPageNumber === totalPages);

      if (this.currentOutput) this.currentOutput.textContent = String(currentPageNumber);
      if (this.totalOutput) this.totalOutput.textContent = String(totalPages);

      if (this.progress) {
        this.progress.style.setProperty('--gh-category-progress', String(currentPageNumber / totalPages));
        this.progress.setAttribute('aria-valuemax', String(totalPages));
        this.progress.setAttribute('aria-valuenow', String(currentPageNumber));
        this.progress.setAttribute('aria-valuetext', `${currentPageNumber} / ${totalPages}`);
      }

      if (this.status) {
        const label = this.status.dataset.label || 'Testimonials';
        const message = `${label}: ${currentPageNumber} / ${totalPages}`;
        if (this.status.textContent !== message) this.status.textContent = message;
      }
    }

    setButtonState(button, disabled) {
      if (!button) return;

      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }
  }

  customElements.define(elementName, GhTestimonialSlider);
})();
